<template>
  <v-app>
    <v-navigation-drawer
      app
      dark
      color="#0f306c"
      v-model="drawer"
      :permanent="isDesktop"
      :temporary="!isDesktop"
      :right="$i18n.locale === 'ar'"
    >
      <v-list dense v-if="permissions.length">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :to="item.route"
          link
          exact
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-spacer></v-spacer>

      <v-list>
        <v-list-item @click="handleLogout" link>
          <v-list-item-icon><v-icon>mdi-logout</v-icon></v-list-item-icon>
          <v-list-item-title>{{ $t("logout") }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app color="white">
      <v-toolbar-title>
        <v-btn icon @click="toggleDrawer" v-if="!isDesktop">
          <v-icon>mdi-menu</v-icon>
        </v-btn>
      </v-toolbar-title>
      <v-spacer></v-spacer>
       <v-btn 
        icon 
        @click="$router.push('/dashboard/password')" 
        class="mx-2"
        :title="$t('change_password')"
      >
        <v-icon>mdi-key-change</v-icon>
      </v-btn>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" text class="mx-4">
            <v-avatar size="24" tile>
              <img :src="flags[$i18n.locale]" alt="flag" class="mx-2" />
            </v-avatar>
            <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-list dense>
          <v-list-item
            v-for="lang in languages"
            :key="lang.code"
            @click="changeLang(lang.code)"
          >
            <v-list-item-avatar tile size="24">
              <img :src="flags[lang.code]" :alt="lang.name" />
            </v-list-item-avatar>
            <v-list-item-title>{{ lang.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <span class="ml-2 font-weight-medium">
        {{ user.first_name }} {{ user.last_name }}
      </span>
    </v-app-bar>

    <v-main>
      <v-container fluid>
        <router-view :key="$route.fullPath" />
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import api from "../services/axios";
import { mapGetters, mapActions } from "vuex";
export default {
  name: "AuthenticatedLayout",
  data() {
    return {
      languages: [
        { code: "en", name: "English" },
        { code: "ar", name: "العربية" },
      ],
      flags: {
        en: "https://flagcdn.com/w40/gb.png",
        ar: "https://flagcdn.com/w40/sa.png",
      },
      drawer: true,
      isDesktop: true,
      menuItems: [
        { title: "home", icon: "mdi-home", route: "/dashboard/home" },
        {
          title: "users",
          icon: "mdi-account-multiple",
          route: "/dashboard/users",
        },
        {
          title: "categories",
          icon: "mdi-shape",
          route: "/dashboard/categories",
        },
        {
          title: "products",
          icon: "mdi-cart-plus",
          route: "/dashboard/products",
        },
        {
          title: "return_product",
          icon: "mdi-cart-minus",
          route: "/dashboard/return-product",
        },
        { title: "sell", icon: "mdi-cash-register", route: "/dashboard/sell" },
        {
          title: "inventory",
          icon: "mdi-warehouse",
          route: "/dashboard/inventory",
        },
        {
          title: "inventory_reconciliation",
          icon: "mdi-scale-balance",
          route: "/dashboard/inventory-reconciliation",
          adminOnly: true,
        },
        {
          title: "reports",
          icon: "mdi-chart-line",
          route: "/dashboard/reports",
        },
        {
          title: "expenses",
          icon: "mdi-cash-minus",
          route: "/dashboard/expenses",
        },
        {
          title: "clientPoints",
          icon: "mdi-star-check",
          route: "/dashboard/clients-points",
        },
        {
          title: "expired_products",
          icon: "mdi-cart",
          route: "/dashboard/expired-products",
        },
        // { title: "deals", icon: "mdi-sale", route: "/dashboard/deals" },
      ],
      permissions: [],
    };
  },
  computed: {
    ...mapGetters(["user"]),
  },
  mounted() {
    this.getPermissions();
    const saved = localStorage.getItem("lang");
    if (saved) this.changeLang(saved);
    this.checkScreen();
    window.addEventListener("resize", this.checkScreen);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkScreen);
  },
  methods: {
    ...mapActions(["logout"]),
    async handleLogout() {
      try {
        await api.get("/user/logout");
        this.logout();
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        this.$router.push("/login");
      } catch (err) {
        console.error("Invalid credentials");
      }
    },
    changeLang(lang) {
      this.$i18n.locale = lang;
      document.dir = lang === "ar" ? "rtl" : "ltr";
      this.$vuetify.rtl = lang === "ar";
      localStorage.setItem("lang", lang);
    },
    async getPermissions() {
      try {
        const res = await api.get("/user");
        this.permissions = res.data.data.permission;
        const permissionMap = {
          Home: "home",
          User: "users",
          Product: "products",
          Category: "categories",
          Sell: "sell",
          Inventory: "inventory",
          Report: "reports",
          Expense: "expenses",
          ClientPoint: "clientPoints",
          Discount: "deals",
          ExpiredProduct: "expired_products",
          ReturnProduct: "return_product"
        };

        this.menuItems = this.menuItems.filter((item) => {
          if (item.adminOnly && this.user.role_id !== 1) {
            return false;
          }

          const perm = this.permissions.find(
            (p) => permissionMap[p.name] === item.title
          );
          return perm ? perm.permission === 1 : true;
        });
      } catch (error) {
        console.error("Failed to load permissions:", error);
      }
    },

    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    checkScreen() {
      this.isDesktop = window.innerWidth >= 1024;
    },
  },
};
</script>

<style scoped>
.v-list-item--active {
  background-color: #fbc02d !important;
  color: #000 !important;
}
</style>
